// Utilities extracted from CoachMarksModule.js

import update from "immutability-helper";
import React from "react";
import { colors, fontStyle } from "Constants";
import { TickCircleFilled } from "@toddle-edu/ds-icons";
import ACLStore from "lib/aclStore";
import { Trans } from "react-i18next";
import ACLStorage from "lib/aclService";
import {
  SNPv2_MANAGE_EVALUATION_CYCLES_PERMISSION,
  SNPv2_VISITOR_PORTAL_PERM_GROUP,
} from "Constants/permissionConstants";

const snpCoachMarksButtonLabelStyles = {
  ...fontStyle.medium,
  fontSize: "1.2rem",
};

const snpCoachMarksTheme = {
  containerStyle: {
    borderRadius: "16px",
  },
  titleStyle: {
    fontSize: "1.8rem",
  },
  buttonContainerStyle: {
    gap: "32px",
  },
  skipButtonStyle: snpCoachMarksButtonLabelStyles,
  nextButtonStyle: {
    ...snpCoachMarksButtonLabelStyles,
    borderRadius: "4px",
    height: "40px",
  },
};

const roundedContainerStyle = {
  borderRadius: "24px",
  boxShadow: "0 2px 24px 0 rgba(0, 0, 0, 0.2)",
  maxWidth: "400px",
};

const colorTitleStyle = {
  color: colors.pink200,
};

const buttonContainerStyle = {
  justifyContent: "flex-start",
  width: "100%",
};

const nextButtonStyle = {
  backgroundColor: colors.pink200,
};

const PREMIUM_ALLOWED_COACHMARKS = [
  "HELP_CENTER",
  "IMPORT_LE_FROM_COMMUNITY_CLASSROOM",
  "IMPORT_LE_FROM_COMMUNITY_UNIT_PLAN",
  "GO_TO_COMMUNITY_BUTTON",
  "TODDLE_ACADEMY_BUTTON",
  "LANGUAGE_SWITCHER_BUTTON",
  "PLATFORM_SWITCHER_V2_BUTTON",
  "PLATFORM_SWITCHER_SCHOOL_ONBOARDING",
  "DASHBOARD_NOTIFICATION_CENTER",
  "SNP_MODULE",
  "SNP_DASHBOARD_NAVIGATION",
  "SNP_STANDARD_STATUS_TICKS",
  "SNP_DASHBOARD_EVIDENCES",
  "SNP_INVITE_VISITING_TEAM",
  "SNP_CHAT_MODULE",
  "SNP_DOCUMENT_CATEGORY_NAVIGATION",
  "SCOPE_SEQUENCE_VIEWER",
  "SCOPE_SEQUENCE_EDITOR",
];

//get all portaltypes for which perticular coachmark should reset
// (Like Unit plan should reset once shown in any portal)
export const getCoachmarkPortalTypes = ({ type, portalType }) => {
  switch (type) {
    case "UNIT_PLAN":
      return ["PLANNER", "COMMUNITY"];
  }
  return [portalType];
};

// a - initial offset, d - interval, x - visits
export const isMemberOfSeries = ({ a, d, x }) => {
  return (x - a) % d == 0 && (x - a) / d >= 0;
};

const getDefaultCoachmarkProperties = ({
  placement = "bottom-end",
  disableBeacon = true,
  placementBeacon = "top",
  spotlightClicks = false,
  isFixed = true,
  showNextButton = false,
} = {}) => {
  const theme = {
    containerStyle: roundedContainerStyle,
    titleStyle: colorTitleStyle,
    buttonContainerStyle: buttonContainerStyle,
    nextButtonStyle: nextButtonStyle,
  };

  return {
    placement,
    disableBeacon,
    placementBeacon,
    spotlightClicks,
    isFixed,
    theme,
    showNextButton,
  };
};

export const getCommunityShareObject = ({ params }) => {
  return () => {
    const { target } = params;
    return {
      steps: [
        {
          ...getDefaultCoachmarkProperties({ showNextButton: false }),
          title: ({ t }) => t("blockScreen:community_share_title"),
          content: ({ t }) => t("blockScreen:community_share_content"),
          target: target,
        },
      ],
    };
  };
};

const _getGuidanceTabContentLocale = () => {
  const isVisitor = ACLStore.can(SNPv2_VISITOR_PORTAL_PERM_GROUP);

  if (isVisitor) {
    return "blockScreen:snp_guidance_tab_content_for_visitor";
  }

  return "blockScreen:snp_guidance_tab_content_for_manage_cycle_access";
};

const _getDashboardTabContentLocale = () => {
  const isVisitor = ACLStore.can(SNPv2_VISITOR_PORTAL_PERM_GROUP);

  if (isVisitor) {
    return "blockScreen:snp_dashboard_tab_content_for_visitor";
  }

  return "blockScreen:snp_dashboard_tab_content_for_manage_cycle_access";
};

const _getDocumentsTabContentLocale = () => {
  const isVisitor = ACLStore.can(SNPv2_VISITOR_PORTAL_PERM_GROUP);

  if (isVisitor) {
    return "blockScreen:snp_documents_tab_content_for_visitor";
  }

  return "blockScreen:snp_documents_tab_content_for_manage_cycle_access";
};

const _getSnpDocumentsTabTitleLocale = () => {
  const isVisitor = ACLStore.can(SNPv2_VISITOR_PORTAL_PERM_GROUP);

  if (isVisitor) {
    return "blockScreen:snp_documents_navigation_title_2_for_visitor";
  }

  return "blockScreen:snp_documents_navigation_title_2_for_manage_cycle_access";
};

const _getSnpDocumentsTabContentLocale = () => {
  const isVisitor = ACLStore.can(SNPv2_VISITOR_PORTAL_PERM_GROUP);

  if (isVisitor) {
    return "blockScreen:snp_documents_navigation_content_2_for_visitor";
  }

  return "blockScreen:snp_documents_navigation_content_2_for_manage_cycle_access";
};

export const getSnpDocumentsNavigationCoachMarksObject = () => {
  return () => {
    const canManageEvaluationCycles = ACLStorage.getPermissionValue(
      SNPv2_MANAGE_EVALUATION_CYCLES_PERMISSION
    );

    let steps = [
      {
        title: ({ t }) =>
          t("blockScreen:snp_common_documents_navigation_title_1"),
        content: ({ t }) =>
          t(`blockScreen:snp_common_documents_navigation_content_1`),
        target: "#COACHMARKS_SNP_DOCUMENTS_SIDEBAR",
        placement: "right-start",
        placementBeacon: "top",
        showProgress: true,
        stepIndex: 0,
        theme: snpCoachMarksTheme,
      },
      {
        title: ({ t }) => t(_getSnpDocumentsTabTitleLocale()),
        content: ({ t }) => t(_getSnpDocumentsTabContentLocale()),
        target: "#COACHMARKS_SNP_DOCUMENTS_SIDEBAR",
        placement: "right",
        disableBeacon: true,
        stepIndex: 1,
        theme: snpCoachMarksTheme,
      },
    ];

    if (canManageEvaluationCycles) {
      steps = update(steps, {
        $push: [
          {
            title: ({ t }) =>
              t(`blockScreen:snp_admin_documents_navigation_title_3`),
            content: ({ t }) =>
              t(`blockScreen:snp_admin_documents_navigation_content_3`),
            target: "#COACHMARKS_SNP_DOCUMENTS_SIDEBAR",
            placement: "right-end",
            disableBeacon: true,
            stepIndex: 2,
            theme: snpCoachMarksTheme,
          },
        ],
      });
    }

    return {
      steps,
      startCountIndex: 0,
      beaconStyles: {
        position: "absolute",
        top: 46,
        left: 92,
      },
    };
  };
};

export const getSnpStandardStatusCoachMarksObject = () => {
  return () => {
    const isVisitor = ACLStore.can(SNPv2_VISITOR_PORTAL_PERM_GROUP);

    const TickCircleEmpty = () => (
      <div
        style={{
          width: "15px",
          height: "15px",
          borderRadius: "50%",
          display: "inline-block",
          border: `1px solid ${colors.borderDark}`,
        }}
      />
    );

    const content = () => {
      return (
        <div>
          {!isVisitor ? (
            <Trans i18nKey={`blockScreen:snp_admin_standard_status_content`}>
              Once you’re done adding all the required evidences for a standard,
              you can mark it done by clicking on the
              <TickCircleEmpty /> icon.
            </Trans>
          ) : (
            <Trans i18nKey={`blockScreen:snp_visitor_standard_status_content`}>
              Once you’re done reviewing the required evidences for a standard,
              you can mark it done by clicking on the
              <TickCircleFilled size={"xx-small"} variant={"subtle"} /> icon.
              This is visible only to the members of the visiting team.
            </Trans>
          )}
        </div>
      );
    };

    return {
      steps: [
        {
          title: ({ t }) => t("blockScreen:snp_common_standard_status_title"),
          content,
          placement: "right-start",
          placementBeacon: "right",
          target: "#COACHMARKS_SNP_SIDEBAR_TICK",
          theme: snpCoachMarksTheme,
          spotlightClicks: true,
        },
      ],
      beaconStyles: {
        marginTop: "-8px",
      },
    };
  };
};

export const getSnpInviteVisitingTeamCoachMarksObject = () => {
  return () => {
    return {
      steps: [
        {
          title: ({ t }) => t("blockScreen:snp_invite_visiting_team_title"),
          content: ({ t }) => t("blockScreen:snp_invite_visiting_team_content"),
          placement: "bottom-end",
          placementBeacon: "right",
          target: "#COACHMARKS_SNP_INVITE_VISITING_TEAM",
          theme: snpCoachMarksTheme,
          isFixed: true,
        },
      ],
      beaconStyles: {
        marginTop: "-42px",
      },
      zIndex: 1003,
    };
  };
};

const _getSnpChatContentLocaleForStep1 = () => {
  const isVisitor = ACLStore.can(SNPv2_VISITOR_PORTAL_PERM_GROUP);

  const canManageEvaluationCycles = ACLStorage.getPermissionValue(
    SNPv2_MANAGE_EVALUATION_CYCLES_PERMISSION
  );

  if (isVisitor) {
    return "blockScreen:snp_chat_content_for_visitor";
  }

  if (canManageEvaluationCycles) {
    return "blockScreen:snp_chat_content_for_manage_cycle_access";
  }

  return "blockScreen:snp_chat_content_for_view_and_evidence_suggestion_access";
};

const _getSnpChatContentLocaleForStep2 = () => {
  const isVisitor = ACLStore.can(SNPv2_VISITOR_PORTAL_PERM_GROUP);

  if (isVisitor) {
    return "blockScreen:snp_chat_content_2_for_visitor";
  }

  return "blockScreen:snp_chat_content_2_for_manage_cycle_access";
};

export const getSnpChatModuleCoachMarksObject = () => {
  return () => {
    return {
      steps: [
        {
          title: ({ t }) => t("blockScreen:snp_common_chat_title"),
          content: ({ t }) => t(_getSnpChatContentLocaleForStep1()),
          target: "#COACHMARKS_SNP_CHAT",
          placement: "left-start",
          placementBeacon: "right",
          stepIndex: 0,
          showProgress: true,
          theme: snpCoachMarksTheme,
        },
        {
          title: ({ t }) => t("blockScreen:snp_common_chat_title_2"),
          content: ({ t }) => t(_getSnpChatContentLocaleForStep2()),
          target: "#COACHMARKS_SNP_CHAT_HEADER",
          placement: "left-start",
          disableBeacon: true,
          stepIndex: 1,
          theme: snpCoachMarksTheme,
          spotlightClicks: true,
        },
      ],
      startCountIndex: 0,
      beaconStyles: {
        marginTop: "-20px",
      },
    };
  };
};

// Auto-generated exports
export {
  snpCoachMarksButtonLabelStyles,
  snpCoachMarksTheme,
  roundedContainerStyle,
  colorTitleStyle,
  buttonContainerStyle,
  nextButtonStyle,
  PREMIUM_ALLOWED_COACHMARKS,
  getDefaultCoachmarkProperties,
  _getGuidanceTabContentLocale,
  _getDashboardTabContentLocale,
  _getDocumentsTabContentLocale,
  _getSnpDocumentsTabTitleLocale,
  _getSnpDocumentsTabContentLocale,
  _getSnpChatContentLocaleForStep1,
  _getSnpChatContentLocaleForStep2,
};
